'use client'

import MuiButton, { ButtonProps } from '@mui/material/Button'
import { forwardRef, MouseEventHandler } from 'react'
import { tss } from 'tss-react/mui'

// Allowing this import because it's just a type and won't affect tree-shaking the JS bundles

import { IconType } from '../Icon'

export type IconButtonProps = {
    Icon: IconType
    variant?: 'default' | 'outlined' | 'contained' | 'overlay'
    /**
     * Nudge the button to the left/right to make it appear aligned with content above or below.
     * Applies only to the default variant, which has no background or border and may appear inset.
     */
    edge?: 'start' | 'end' | false
    'aria-label': string
    onClick?: MouseEventHandler
    onMouseDown?: MouseEventHandler
    onMouseUp?: MouseEventHandler
    className?: string
    'data-testid'?: string
    disabled?: boolean
    href?: string
    target?: string
    inverted?: boolean
    sx?: ButtonProps['sx']
}

const CLASS_PREFIX = 'IconButton'
const muiClasses = {
    root: `${CLASS_PREFIX}-root`,
    rounded: `${CLASS_PREFIX}-rounded`,
    edgeStart: `${CLASS_PREFIX}-edgeStart`,
    edgeEnd: `${CLASS_PREFIX}-edgeEnd`,
    overlay: `${CLASS_PREFIX}-overlay`,
}

const useStyles = tss.create(({ theme }) => ({
    main: {
        [`&.${muiClasses.root}`]: {
            padding: theme.spacing(1),
            minWidth: 40,
        },
        [`&.${muiClasses.rounded}`]: {
            borderRadius: '50%',
        },
        [`&.${muiClasses.edgeStart}`]: {
            marginLeft: -12,
        },
        [`&.${muiClasses.edgeEnd}`]: {
            marginRight: -12,
        },
        [`&.${muiClasses.overlay}`]: {
            '&, &:hover': {
                backgroundColor: theme.palette.action.active,
            },
            '&:disabled': {
                backgroundColor: theme.palette.action.disabledBackground,
            },
        },
    },
}))

const IconButton = forwardRef<null, IconButtonProps>(function IconButton_(
    {
        Icon,
        variant = 'default',
        edge = false,
        onClick,
        className = '',
        disabled = false,
        inverted = false,
        sx,
        ...props
    },
    ref
) {
    const { classes, cx } = useStyles()

    if (variant === 'contained' || variant === 'outlined') {
        return (
            <MuiButton
                {...props}
                onClick={onClick}
                variant={variant}
                color="primary"
                disableElevation
                aria-label={props['aria-label']}
                className={cx(classes.main, muiClasses.root, className)}
                sx={sx}
                disabled={disabled}
                ref={ref}
            >
                <Icon />
            </MuiButton>
        )
    }

    if (variant === 'overlay') {
        return (
            <MuiButton
                {...props}
                onClick={onClick}
                variant="text"
                aria-label={props['aria-label']}
                className={cx(classes.main, muiClasses.root, muiClasses.rounded, muiClasses.overlay, className)}
                sx={sx}
                disabled={disabled}
                ref={ref}
            >
                <Icon color={disabled ? 'action.disabled' : 'common.white'} />
            </MuiButton>
        )
    }

    const iconActiveColor = inverted ? 'common.white' : 'action.active'

    return (
        <MuiButton
            {...props}
            onClick={onClick}
            variant="text"
            aria-label={props['aria-label']}
            className={cx(
                classes.main,
                muiClasses.root,
                muiClasses.rounded,
                edge === 'start' && muiClasses.edgeStart,
                edge === 'end' && muiClasses.edgeEnd,
                className
            )}
            sx={sx}
            disabled={disabled}
            ref={ref}
        >
            <Icon color={disabled ? 'action.disabled' : iconActiveColor} />
        </MuiButton>
    )
})

export default IconButton
